// Device location for Buddy (distances, ETA, route of the day).
// Native app: @capacitor/geolocation (iOS/Android permission prompts); web: navigator.geolocation.
import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";
import { App as CapApp } from "@capacitor/app";

let last = null; // { lat, lng, ts }
const FRESH_MS = 90 * 1000;

const remember = (pos) => {
  last = { lat: pos.coords.latitude, lng: pos.coords.longitude, ts: Date.now() };
  return last;
};

/** Cached fix if recent, else null (never prompts). */
export function cachedPosition() {
  return last && Date.now() - last.ts < FRESH_MS ? last : null;
}

/** true when the OS already granted location (no prompt is shown). */
export async function locationGranted() {
  try {
    if (Capacitor.isNativePlatform()) {
      const st = await Geolocation.checkPermissions();
      return st.location === "granted" || st.coarseLocation === "granted";
    }
    if (navigator.permissions?.query) {
      const st = await navigator.permissions.query({ name: "geolocation" });
      return st.state === "granted";
    }
  } catch (e) { /* ignore */ }
  return false;
}

/** Current position. prompt=true asks the OS permission when needed (call from a user flow). */
export async function getPosition({ prompt = false, timeoutMs = 8000 } = {}) {
  const fresh = cachedPosition();
  if (fresh) return fresh;
  try {
    if (Capacitor.isNativePlatform()) {
      let st = await Geolocation.checkPermissions();
      if (st.location !== "granted" && st.coarseLocation !== "granted") {
        if (!prompt) return null;
        st = await Geolocation.requestPermissions();
        if (st.location !== "granted" && st.coarseLocation !== "granted") return null;
      }
      const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: timeoutMs, maximumAge: FRESH_MS });
      return remember(pos);
    }
    if (!navigator.geolocation) return null;
    if (!prompt && !(await locationGranted())) return null;
    const pos = await new Promise((res, rej) =>
      navigator.geolocation.getCurrentPosition(res, rej, { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: FRESH_MS }));
    return remember(pos);
  } catch (e) {
    return null;
  }
}

/** Refresh the cached fix in the background when permission already exists. */
export function warmPosition() {
  getPosition({ prompt: false }).catch(() => {});
}

/** Opens a maps universal link in the SYSTEM (Google Maps / Waze / Apple Maps app when installed). */
export async function openExternal(url) {
  try {
    if (Capacitor.isNativePlatform()) { await CapApp.openUrl({ url }); return; }
  } catch (e) { /* fall through */ }
  const w = window.open(url, "_blank", "noopener");
  if (!w) window.location.href = url;
}
